import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createVNode as _createVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "my-2 md:mb-4" }
const _hoisted_2 = { class: "flex items-center justify-between px-2" }
const _hoisted_3 = { class: "md:text-lg leading-4 font-semibold text-gray-900" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 0 }
const _hoisted_6 = { class: "md:text-center pl-2 pt-2 md:p-0 md:text-base text-sm" }
const _hoisted_7 = {
  key: 0,
  class: "py-1 hidden md:flex gap-2 md:gap-4 px-2 border-b"
}
const _hoisted_8 = { class: "font-medium truncate text-gray-600 w-3/12" }
const _hoisted_9 = { class: "font-medium truncate text-gray-600 w-2/12" }
const _hoisted_10 = { class: "font-medium truncate text-gray-600 w-2/12" }
const _hoisted_11 = { class: "font-medium truncate text-gray-600 w-3/12" }
const _hoisted_12 = { class: "font-medium truncate text-gray-600 w-2/12" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("div", { class: "w-6" }, [
  /*#__PURE__*/_createElementVNode("span", { class: "sr-only" }, "delete")
], -1)
const _hoisted_14 = {
  key: 0,
  class: "flex flex-wrap md:flex-nowrap px-2 py-2 gap-2 md:gap-4 relative tableRow border-gray-200 items-center"
}
const _hoisted_15 = { class: "w-3/12 relative" }
const _hoisted_16 = ["onUpdate:modelValue", "placeholder"]
const _hoisted_17 = {
  key: 1,
  class: "text-sm"
}
const _hoisted_18 = {
  key: 2,
  class: "text-red absolute left-0",
  style: {"top":"calc(100% + 4px)"}
}
const _hoisted_19 = { class: "w-2/12" }
const _hoisted_20 = { class: "w-2/12" }
const _hoisted_21 = { class: "w-3/12 flex" }
const _hoisted_22 = ["onUpdate:modelValue", "placeholder"]
const _hoisted_23 = {
  key: 1,
  class: "text-xs"
}
const _hoisted_24 = { class: "w-2/12 flex" }
const _hoisted_25 = /*#__PURE__*/_createElementVNode("span", {
  "aria-hidden": "true",
  class: "pointer-events-none absolute h-full w-full rounded-md"
}, null, -1)
const _hoisted_26 = { class: "w-6 order-first md:order-last" }
const _hoisted_27 = { class: "flex justify-center w-full md:mt-6" }

import { Switch } from "@headlessui/vue";
import { PlusIcon, PencilIcon, XMarkIcon, CalendarIcon } from "@heroicons/vue/24/solid";
import VueDatePicker from "@vuepic/vue-datepicker";
import { format } from "date-fns";
import deepEqual from "deep-equal";
import { watch, PropType, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirmationModal } from "shared/composables/toast";
import { NonWorkingDay } from "shared/types/ProjectDurationSettings";
import CustomOptionsCombobox from "@/components/other/CustomOptionsCombobox.vue";
import { disturbances } from "@/constants/disturbances";


export default /*#__PURE__*/_defineComponent({
  __name: 'DisturbancesSection',
  props: {
  nonWorkingDays: {
    type: Array as PropType<NonWorkingDay[]>,
    required: true,
  },
  approved: {
    type: Boolean,
    required: true,
  },
  editMode: {
    type: Boolean,
    required: true,
  },
  highlightSection: {
    type: String,
    required: true,
  },
  date: {
    type: Date,
    required: true,
  },
  validationError: {
    type: Boolean,
    required: true,
  },
},
  emits: ["addHighlight", "activeEditMode"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;
const showConfirmationModal = useConfirmationModal();

const disturbancesToShow = ref<(NonWorkingDay & { show: boolean })[]>([]);

watch(
  () => props.nonWorkingDays,
  (value) => {
    const currentDate = format(props.date, "yyyy-MM-dd");
    disturbancesToShow.value = value.map((nonWorkingDay) => {
      const rangeStart = format(nonWorkingDay.start_date, "yyyy-MM-dd");
      const rangeEnd = format(nonWorkingDay.end_date, "yyyy-MM-dd");

      const shouldShow =
        nonWorkingDay.type === "disturbance" &&
        rangeStart <= currentDate &&
        rangeEnd >= currentDate;

      return {
        ...nonWorkingDay,
        show: shouldShow,
      };
    });
  },
  { immediate: true },
);

const { t, te, locale } = useI18n();

const areDisturbancesChanges = computed(() => {
  return !deepEqual(
    disturbancesToShow.value.map((disturbance) => {
      const { show, ...rest } = disturbance;
      return rest;
    }),
    props.nonWorkingDays,
  );
});

const activeEditMode = () => {
  emit("addHighlight");
  emit("activeEditMode");
};

const addNewDisturbance = () => {
  activeEditMode();

  disturbancesToShow.value = [
    ...disturbancesToShow.value,
    {
      name: "",
      type: "disturbance",
      start_date: props.date,
      end_date: props.date,
      disturbance_type: "",
      disturbance_description: "",
      is_critical: false,
      show: true,
    },
  ];
};

const handleDisturbanceDateRangeChange = (value: [Date, Date] | [null, null], index: number) => {
  if (!value[0] || !value[1]) {
    return;
  }

  const updatedDisturbances = disturbancesToShow.value.map((disturbance, i) => {
    if (i === index) {
      return {
        ...disturbancesToShow.value[index],
        start_date: value[0],
        end_date: value[1],
      };
    }

    return disturbance;
  });

  disturbancesToShow.value = updatedDisturbances;
};

const handleDeleteDisturbance = async (disturbance: NonWorkingDay, index: number) => {
  let isConfirmed = true;
  if (disturbance.start_date !== disturbance.end_date) {
    isConfirmed = await showConfirmationModal({
      message: t("report.sections.disturbances.confirmation_message"),
      header: t("report.sections.disturbances.confirmation_header"),
      confirmAction: t("report.sections.disturbances.delete_action"),
      cancelAction: t("report.sections.disturbances.cancel_action"),
    });
  }

  if (isConfirmed) {
    const updatedDisturbances = disturbancesToShow.value.filter((_, i) => i !== index);
    disturbancesToShow.value = updatedDisturbances;
  }
};

const formatDate = (date: Date) => {
  return format(date, "dd.MM.yyyy");
};

const getDisturbances = () => {
  return disturbancesToShow.value.map((disturbance) => {
    const { show, ...rest } = disturbance;
    return rest;
  });
};

const areDisturbancesValid = () => {
  return disturbancesToShow.value.every((disturbance) => {
    return disturbance.show
      ? disturbance.name &&
          disturbance.disturbance_type &&
          disturbance.start_date <= disturbance.end_date
      : true;
  });
};

__expose({
  getData: getDisturbances,
  isValid: areDisturbancesValid,
  isDirty: areDisturbancesChanges,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    onClick: _cache[1] || (_cache[1] = ($event: any) => (__props.editMode ? emit('addHighlight') : '')),
    class: _normalizeClass([
      {
        'shadow-lg mix-blend-normal bg-blend-color-dodge': __props.editMode,
        'border-yellow': __props.highlightSection === 'disturbances',
      },
      'my-4 bg-white shadow sm:rounded-lg md:px-5 px-1 py-6 border',
    ])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("h3", _hoisted_3, _toDisplayString(_unref(t)("disturbances.disturbance", 2)), 1),
        (!__props.approved)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              (!__props.editMode)
                ? (_openBlock(), _createBlock(_unref(PencilIcon), {
                    key: 0,
                    class: "w-4 h-4 text-oaiGray-300 cursor-pointer ml-3",
                    onClick: activeEditMode
                  }))
                : _createCommentVNode("", true)
            ]))
          : _createCommentVNode("", true)
      ]),
      (__props.approved && disturbancesToShow.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("p", _hoisted_6, _toDisplayString(_unref(t)("report.general.section_empty")), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", {
      class: _normalizeClass([__props.editMode ? 'editInput' : 'overviewInput'])
    }, [
      (disturbancesToShow.value.some((d) => d.show))
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("p", _hoisted_8, _toDisplayString(_unref(t)("report.sections.disturbances.name")), 1),
            _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)("report.sections.disturbances.type")), 1),
            _createElementVNode("p", _hoisted_10, _toDisplayString(_unref(t)("report.sections.disturbances.date_range")), 1),
            _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(t)("report.sections.disturbances.description")), 1),
            _createElementVNode("p", _hoisted_12, _toDisplayString(_unref(t)("working_day.inactive_days", 1)) + "? ", 1),
            _hoisted_13
          ]))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(disturbancesToShow.value, (disturbance, i) => {
        return (_openBlock(), _createElementBlock("div", { key: i }, [
          (disturbance.show)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  (__props.editMode)
                    ? _withDirectives((_openBlock(), _createElementBlock("input", {
                        key: 0,
                        "onUpdate:modelValue": ($event: any) => ((disturbance.name) = $event),
                        type: "text",
                        placeholder: _unref(t)('report.sections.disturbances.name'),
                        class: _normalizeClass({ 'oai-inputs': __props.editMode })
                      }, null, 10, _hoisted_16)), [
                        [_vModelText, disturbance.name]
                      ])
                    : (_openBlock(), _createElementBlock("p", _hoisted_17, _toDisplayString(disturbance.name || "-"), 1)),
                  (__props.validationError && !disturbance.name)
                    ? (_openBlock(), _createElementBlock("small", _hoisted_18, _toDisplayString(_unref(t)("err.required")), 1))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_19, [
                  _createVNode(CustomOptionsCombobox, {
                    disabled: !__props.editMode,
                    options: 
                _unref(disturbances).map((d) => ({
                  name: _unref(te)(`disturbances.type.${d}`) ? _unref(t)(`disturbances.type.${d}`) : d,
                  value: d,
                }))
              ,
                    selected: disturbance.disturbance_type as string,
                    error: __props.validationError && !disturbance.disturbance_type,
                    onUpdate: ($event: any) => (disturbancesToShow.value[i].disturbance_type = $event.value)
                  }, null, 8, ["disabled", "options", "selected", "error", "onUpdate"])
                ]),
                _createElementVNode("div", _hoisted_20, [
                  _createVNode(_unref(VueDatePicker), {
                    columns: 4,
                    placeholder: `${_unref(t)('report.bulk_export.main_label')}`,
                    locale: _unref(locale),
                    format: "dd.MM.yyyy",
                    autoApply: "",
                    modelValue: [disturbance.start_date, disturbance.end_date],
                    range: "",
                    enableTimePicker: false,
                    disabled: !__props.editMode,
                    "onUpdate:modelValue": ($event: any) => (handleDisturbanceDateRangeChange($event, i))
                  }, {
                    trigger: _withCtx(() => [
                      _createElementVNode("p", {
                        class: _normalizeClass([
                    'rounded-md text-xs py-2 bg-white flex justify-between',
                    __props.editMode && 'shadow-sm cursor-pointer border border-gray-300 px-3',
                  ])
                      }, [
                        _createElementVNode("span", null, _toDisplayString(formatDate(disturbance.start_date)) + " - " + _toDisplayString(formatDate(disturbance.end_date)), 1),
                        (__props.editMode)
                          ? (_openBlock(), _createBlock(_unref(CalendarIcon), {
                              key: 0,
                              class: "w-4 h-4 text-gray-400"
                            }))
                          : _createCommentVNode("", true)
                      ], 2)
                    ]),
                    _: 2
                  }, 1032, ["placeholder", "locale", "modelValue", "disabled", "onUpdate:modelValue"])
                ]),
                _createElementVNode("div", _hoisted_21, [
                  (__props.editMode)
                    ? _withDirectives((_openBlock(), _createElementBlock("textarea", {
                        key: 0,
                        "onUpdate:modelValue": ($event: any) => ((disturbance.disturbance_description) = $event),
                        rows: "2",
                        name: "note",
                        placeholder: _unref(t)('report.general.notes_placeholder'),
                        class: _normalizeClass([{ 'oai-inputs': __props.editMode }, 'col-span-2'])
                      }, null, 10, _hoisted_22)), [
                        [_vModelText, disturbance.disturbance_description]
                      ])
                    : (_openBlock(), _createElementBlock("p", _hoisted_23, _toDisplayString(disturbance.disturbance_description || "-"), 1))
                ]),
                _createElementVNode("div", _hoisted_24, [
                  _createVNode(_unref(Switch), {
                    modelValue: disturbance.is_critical,
                    "onUpdate:modelValue": [
                      ($event: any) => ((disturbance.is_critical) = $event),
                      _cache[0] || (_cache[0] = ($event: any) => (!__props.editMode && activeEditMode()))
                    ],
                    class: _normalizeClass([
                'group relative inline-flex h-5 w-10 shrink-0 items-center justify-center rounded-full focus:outline-none mr-2',
                __props.editMode && 'cursor-pointer',
              ])
                  }, {
                    default: _withCtx(() => [
                      _hoisted_25,
                      _createElementVNode("span", {
                        "aria-hidden": "true",
                        class: _normalizeClass([
                  disturbance.is_critical ? 'bg-yellow-500' : 'bg-gray-200',
                  'pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out',
                ])
                      }, null, 2),
                      _createElementVNode("span", {
                        "aria-hidden": "true",
                        class: _normalizeClass([
                  disturbance.is_critical ? 'translate-x-5' : 'translate-x-0',
                  'pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out',
                ])
                      }, null, 2)
                    ]),
                    _: 2
                  }, 1032, ["modelValue", "onUpdate:modelValue", "class"])
                ]),
                _createElementVNode("button", _hoisted_26, [
                  (__props.editMode)
                    ? (_openBlock(), _createBlock(_unref(XMarkIcon), {
                        key: 0,
                        class: _normalizeClass({ 'w-6 text-red-900 hover:text-red': __props.editMode }),
                        onClick: ($event: any) => (handleDeleteDisturbance(disturbance, i))
                      }, null, 8, ["class", "onClick"]))
                    : _createCommentVNode("", true)
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      }), 128)),
      _createElementVNode("div", _hoisted_27, [
        ((__props.editMode || !disturbancesToShow.value.some((d) => d.show)) && !__props.approved)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              onClick: addNewDisturbance,
              type: "button",
              class: "relative inline-flex items-center p-2 shadow-sm border-2 border-yellow rounded-full text-yellow-700 hover:bg-yellow-700 hover:text-white focus:outline-none"
            }, [
              _createVNode(_unref(PlusIcon), { class: "w-6" })
            ]))
          : _createCommentVNode("", true)
      ])
    ], 2),
    _createElementVNode("div", {
      class: _normalizeClass(['flex justify-center', { 'mt-5': __props.editMode }])
    }, null, 2)
  ], 2))
}
}

})